import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/api";
import { Logo } from "@/components/Logo";

const Shell = ({ children, testid }) => (
  <div className="relative flex min-h-screen items-center justify-center bg-cosmic-base bg-nebula px-4" data-testid={testid}>
    <div className="grain-overlay" />
    <motion.div
      initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
      className="glass w-full max-w-md rounded-3xl p-10 text-center">
      <div className="flex justify-center mb-6"><Logo size={52} /></div>
      {children}
      <Link to="/" data-testid="payment-result-home"
        className="mt-8 inline-block rounded-full bg-gradient-to-r from-cosmic-pink to-cosmic-purple px-8 py-3 text-sm font-bold text-white">
        Back to Cosmic Cloudz
      </Link>
    </motion.div>
  </div>
);

export function PaymentSuccess() {
  const location = useLocation();
  const sessionId = new URLSearchParams(location.search).get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          clearInterval(timer);
        }
      } catch { /* keep polling */ }
      if (tries >= 15) {
        setState((s) => (s === "paid" ? s : "failed"));
        clearInterval(timer);
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [sessionId]);

  if (state === "checking") {
    return (
      <Shell testid="payment-success-page">
        <Loader2 size={48} className="mx-auto animate-spin text-cosmic-cyan" />
        <h1 className="font-display mt-5 text-2xl font-black text-white">Confirming your payment…</h1>
        <p className="mt-2 text-sm text-slate-400">Hang tight while Stripe beams back the confirmation.</p>
      </Shell>
    );
  }

  if (state === "paid") {
    return (
      <Shell testid="payment-success-page">
        <CheckCircle2 size={56} className="mx-auto text-cosmic-green" />
        <h1 className="font-display mt-5 text-3xl font-black text-white" data-testid="payment-paid-heading">Payment received!</h1>
        <p className="mt-3 text-sm text-slate-300 leading-relaxed">
          Your card payment went through. Your order is locked in — we'll be in touch with the details.
        </p>
      </Shell>
    );
  }

  return (
    <Shell testid="payment-success-page">
      <XCircle size={56} className="mx-auto text-cosmic-yellow" />
      <h1 className="font-display mt-5 text-2xl font-black text-white">Still processing</h1>
      <p className="mt-3 text-sm text-slate-300 leading-relaxed">
        We couldn't confirm the payment just yet. If your card was charged, it will show up shortly — otherwise reach out and we'll sort it out.
      </p>
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell testid="payment-cancel-page">
      <XCircle size={56} className="mx-auto text-cosmic-pink" />
      <h1 className="font-display mt-5 text-2xl font-black text-white">Payment cancelled</h1>
      <p className="mt-3 text-sm text-slate-300 leading-relaxed">
        No charge was made. Your request is still saved — you can pay by card anytime or use Cash, Zelle, Cash App, Venmo, or Apple Pay.
      </p>
    </Shell>
  );
}

export default PaymentSuccess;
